import { useCallback, useEffect, useMemo } from "react"
import {
  useGetBookingsQuery,
  useGetCalUsersQuery,
  useGetAvailabilityQuery,
  useGetTeamEventTypesQuery,
  useGetTeamMembersQuery,
  useGetTeamsQuery,
} from "../bookingCalendarApi"
import type {
  BookingDetails,
  CalOrganizationUser,
  CalTeam,
  CalTeamMember,
} from "../bookingCalendarApi"
import { slotTypeForSlug } from "./slotTypes"
import { memberColumns } from "./staffColumns"
import type { StaffColumn } from "./staffColumns"
import { SITE_TIME_ZONE, dayWindow } from "./siteTime"
import type { CalendarDate } from "./siteTime"
import {
  bookingsOutsideWindow,
  bookingsWithoutColumn,
  buildDayGrid,
  businessHoursFor,
  isLiveBooking,
  unavailabilityByColumn,
} from "./dayGrid"
import type { DayLoadStatus, DaySchedule, UnavailabilityMark } from "./dayGrid"
import { awaitingCurrent, useTrackedRefresh } from "./viewLoading"
import type { BookingSeasonBounds } from "./bookingSeason"
import { firstCalThrottled } from "@/lib/calThrottled"

// Stable identities while a query is still loading: a fresh [] each render would give every memo
// downstream a new identity, and — with the grid rebuilt from them — that is wasted work on every
// keystroke elsewhere in the app.
const NO_BOOKINGS: BookingDetails[] = []
const NO_MEMBERS: CalTeamMember[] = []
const NO_USERS: CalOrganizationUser[] = []
const NO_TEAMS: CalTeam[] = []

/** The default appointment length when a team has no event type to read one from. */
const FALLBACK_SLOT_MINUTES = 30

export interface TeamDay {
  /** Every team in the Cal organization, for the selector. */
  teams: CalTeam[]
  team: CalTeam | undefined
  /** One column per team member, whether or not they have anything booked. */
  columns: StaffColumn[]
  schedule: DaySchedule
  /**
   * The day's bookings as the server returned them, cancelled ones included. For questions the grid
   * cannot answer, like who is already booked while a given appointment runs.
   */
  bookings: readonly BookingDetails[]
  /** Row height and ordinary appointment length, from the team's intake event type. */
  slotMinutes: number
  /**
   * Live (non-cancelled) bookings on this day that a column hosts — the ones the grid can draw.
   * Appointments hosted by nobody on the roster are counted by `unhostedCount` instead: a number
   * here that no column accounts for reads as a grid that has lost appointments.
   */
  bookingCount: number
  /** Of those, the ones the grid has no row for because they fall outside office hours. */
  outsideWindowCount: number
  /**
   * Appointments today hosted by nobody on this team's roster — what deactivation leaves behind,
   * since it takes the person off the team while their appointments stay in Cal.
   */
  unhostedCount: number
  /** Each column with time nobody is working today, keyed by column id. Absent means bookable all day. */
  columnUnavailability: Record<string, UnavailabilityMark>
  /**
   * The availability read failed, so the grid is drawing everyone as bookable whether they are or
   * not. Distinct from `error`: the day itself loaded, and the calendar is worth showing — but it is
   * now over-stating availability, which the page has to say out loud.
   */
  availabilityUnavailable: boolean
  /**
   * "loading" while there is not yet a grid to draw at all — no roster, so no columns. Once there is,
   * a day still on its way is `slotsLoading` instead: the grid's shape is known and its contents are
   * not.
   */
  status: DayLoadStatus
  /**
   * The columns and rows are known but what goes in them is not: this day's appointments or
   * availability are still on their way, or the user has asked for them again. The grid draws its
   * cells as placeholders, and every count above reads as nothing booked — neither is an answer yet.
   */
  slotsLoading: boolean
  error: string | null
  /**
   * The first of this day's reads that Cal throttled (503 `calThrottled`, AB#514), or null. The page
   * shows the busy notice for it in place of `error`, with `refresh` as its retry.
   */
  throttledError: unknown
  /** Set when the team cannot take bookings at all — no slug, or no event type. */
  unavailableReason: string | null
  /**
   * Re-read the day from the server, cache or no cache.
   *
   * Cal is written to from outside this app — by staff in Cal itself, and by households booking on
   * the public site — so a grid left open goes stale without anything here changing to invalidate
   * it. This is the way back to the truth without reloading the page.
   */
  refresh: () => void
  /**
   * A read is in flight, including a refetch of data already on screen — what spins the refresh
   * button. Wider than `slotsLoading`: a refetch after this app's own write keeps the grid up.
   */
  isFetching: boolean
}

/**
 * One team's day: its roster as columns, its bookings laid over a slot grid.
 *
 * Sites are addressed by Cal team slug throughout, which is how the server addresses them — every
 * site publishes the same event type slugs, so the team is what identifies the office.
 *
 * @param bookingSeason When the public may book, to mark the rows they may not. Read by the page
 * rather than here, and undefined while it is unknown — which marks nothing.
 */
export function useTeamDay(
  date: CalendarDate,
  selectedTeamId: number | null,
  bookingSeason?: BookingSeasonBounds,
): TeamDay {
  const teamsQuery = useGetTeamsQuery()
  const teams = teamsQuery.currentData ?? NO_TEAMS

  const team = useMemo(
    () => teams.find(t => t.id === selectedTeamId) ?? teams.find(t => !!t.slug) ?? teams[0],
    [teams, selectedTeamId])

  const teamId = team?.id
  const membersQuery = useGetTeamMembersQuery({ teamId: teamId! }, { skip: teamId == null })
  const eventTypesQuery = useGetTeamEventTypesQuery({ teamId: teamId! }, { skip: teamId == null })
  const usersQuery = useGetCalUsersQuery()

  const timeZone = team?.timeZone || SITE_TIME_ZONE
  const window = useMemo(() => dayWindow(date, timeZone), [date, timeZone])

  // No status filter: the day window is the whole of it. Cal cuts its buckets against now rather
  // than against the window asked for — "upcoming" means "ends in the future" — so asking for one
  // would drop this morning's finished appointments out of today's grid as the day went on, which
  // reads as nobody having been booked.
  const bookingsQuery = useGetBookingsQuery(
    { teamSlugs: team?.slug ? [team.slug] : [], ...window },
    { skip: !team?.slug },
  )

  // `currentData`, never `data`, throughout: `data` is whatever the hook last held for any arguments,
  // so a step to the next day kept drawing the previous day's appointments — and its availability —
  // until the new ones landed, as though they were the answer.
  const members = membersQuery.currentData ?? NO_MEMBERS
  const users = usersQuery.currentData ?? NO_USERS
  const bookings = bookingsQuery.currentData ?? NO_BOOKINGS
  const eventTypes = eventTypesQuery.currentData

  const columns = useMemo(() => memberColumns(members, users), [members, users])

  // The team's intake event type — what the grid's cadence comes from. Not what availability is
  // asked about on its own: see `availabilityEventTypeIds`.
  const intakeEventType = useMemo(
    () => eventTypes?.find(e => e.slug === "intake") ?? eventTypes?.[0],
    [eventTypes])

  // Every event type the site publishes, because its opening hours are split across them: intake's
  // schedule takes Monday to Thursday and off-slot's takes Friday. Asking about intake alone came
  // back with no ranges on a Friday, which the grid drew as the whole site being closed.
  const availabilityEventTypeIds = useMemo(
    () => (eventTypes ?? []).map(e => e.id),
    [eventTypes])

  // Which of those are the managed and private kinds (AB#779). The availability answer above is the
  // union of all of them, so without these a free Friday row — off-slot's day — is indistinguishable
  // from a free public one. The slugs come from the one map in `slotTypes`.
  const offSlotEventTypeIds = useMemo(
    () => (eventTypes ?? []).filter(e => slotTypeForSlug(e.slug) === "managed").map(e => e.id),
    [eventTypes])
  const privateEventTypeIds = useMemo(
    () => (eventTypes ?? []).filter(e => slotTypeForSlug(e.slug) === "private").map(e => e.id),
    [eventTypes])

  // Intake is the bookable kind; off-slot appointments are the exception and must not set the
  // cadence for the whole grid.
  const slotMinutes = intakeEventType?.lengthInMinutes && intakeEventType.lengthInMinutes > 0
    ? intakeEventType.lengthInMinutes
    : FALLBACK_SLOT_MINUTES

  // Asked for by host id, and skipped entirely when there is nobody to ask about: the endpoint reads
  // an empty filter as "every host", so a team with no roster yet would come back holding the whole
  // site's availability.
  const hostIds = useMemo(() => columns.map(column => column.calUserId), [columns])
  const availabilityQuery = useGetAvailabilityQuery(
    { teamId: teamId!, eventTypeIds: availabilityEventTypeIds, userIds: hostIds, ...window },
    { skip: teamId == null || availabilityEventTypeIds.length === 0 || hostIds.length === 0 },
  )

  // Undefined, not an empty array, while it is unknown: the grid reads "no availability" as "nobody
  // is bookable" and would shade the whole day, which is a much stronger claim than "not asked yet".
  const availability = availabilityQuery.currentData

  // An outreach site closes at noon; a main office runs the whole day — each in the window the server
  // sends on the team (`gridWindow`, AB#783), with the constants only for a team that carries none.
  const businessHours = useMemo(() => businessHoursFor([team]), [team])

  const schedule = useMemo(
    () => buildDayGrid(
      { date, bookings, columns, slotMinutes, timeZone, availability, offSlotEventTypeIds, privateEventTypeIds, businessHours, bookingSeason }),
    [date, bookings, columns, slotMinutes, timeZone, availability, offSlotEventTypeIds, privateEventTypeIds, businessHours, bookingSeason])

  const columnUnavailability = useMemo(
    () => unavailabilityByColumn({ date, bookings, columns, slotMinutes, timeZone, availability, businessHours }),
    [date, bookings, columns, slotMinutes, timeZone, availability, businessHours])

  // The grid draws office hours only, so an appointment outside them has no row. It is still the
  // team's appointment, and the toolbar says how many there are rather than losing them.
  const outsideWindow = useMemo(
    () => bookingsOutsideWindow({ date, bookings, columns, slotMinutes, timeZone, businessHours }),
    [date, bookings, columns, slotMinutes, timeZone, businessHours])

  // Appointments with no column to draw them in: their host is not on this team's roster any more,
  // which is the state deactivation leaves — the membership goes, the appointments stay.
  const unhosted = useMemo(
    () => bookingsWithoutColumn({ date, bookings, columns, slotMinutes, timeZone }),
    [date, bookings, columns, slotMinutes, timeZone])

  // A count in the toolbar tells the user something is off; it does not tell anyone *which*
  // appointments, and these are the ones worth chasing — Cal accepted a booking at an hour the
  // office hours here cannot draw. Logged as an error so it surfaces in the console rather than
  // living only as a number nobody can act on.
  useEffect(() => {
    if (outsideWindow.length === 0) return
    console.error(
      `Booking calendar: ${outsideWindow.length} appointment(s) on ${date} fall outside the grid's `
      + `hours for team ${team?.slug ?? "unknown"} and cannot be drawn:`,
      outsideWindow.map(booking => ({
        uid: booking.uid,
        start: booking.start,
        end: booking.end,
      })),
    )
  }, [outsideWindow, date, team?.slug])

  // Same reasoning, different cause: these have an hour the grid could draw and nobody to draw them
  // under, and whoever has to cover them needs their uids rather than a count.
  useEffect(() => {
    if (unhosted.length === 0) return
    console.error(
      `Booking calendar: ${unhosted.length} appointment(s) on ${date} at team `
      + `${team?.slug ?? "unknown"} are hosted by nobody on its roster and cannot be drawn:`,
      unhosted.map(booking => ({
        uid: booking.uid,
        start: booking.start,
        hosts: booking.hosts.map(host => host.id),
      })),
    )
  }, [unhosted, date, team?.slug])

  // The availability read is not in `failed`: losing it costs the day its unavailable marks, not the
  // day itself, and a calendar that refuses to draw teaches nobody anything. It IS in
  // `slotsLoading` — drawing rows as open and correcting them a moment later would show a slot free
  // that never was, which is the one lie this grid must not tell even briefly.
  const failed = !!teamsQuery.error || !!membersQuery.error || !!usersQuery.error || !!bookingsQuery.error

  // What the grid's shape is made of: the columns, and the cadence the rows are cut to.
  const shapeLoading = [teamsQuery, membersQuery, usersQuery, eventTypesQuery].some(awaitingCurrent)

  const loadStatus: DayLoadStatus =
    failed ? "failed"
      : shapeLoading ? "loading"
        : teamsQuery.currentData ? "succeeded"
          : "idle"

  const { refreshing, track: trackRefresh } = useTrackedRefresh(`${teamId ?? ""}:${date}`)

  const slotsLoading = !failed
    && (refreshing || shapeLoading || [bookingsQuery, availabilityQuery].some(awaitingCurrent))

  // Every read the grid is made of, refetched together — the roster included, not just the
  // bookings: somebody added to the team in Cal is as much a change to this day as an appointment
  // is, and a refresh that brought the appointments without the column to draw them in would
  // report them as hosted by former staff.
  //
  // A skipped query has never run, so there is nothing to refetch and asking warns; the ones that
  // skip do so because the team has no slug or no roster yet, and they start themselves once it has.
  const refresh = useCallback(() => {
    trackRefresh(
      [teamsQuery, membersQuery, usersQuery, eventTypesQuery, bookingsQuery, availabilityQuery]
        .filter(query => !query.isUninitialized)
        .map(query => query.refetch()))
  }, [trackRefresh, teamsQuery, membersQuery, usersQuery, eventTypesQuery, bookingsQuery, availabilityQuery])

  // A team with no slug was never provisioned in Cal, and one with no event type publishes no
  // slots. Both draw an empty grid, which on its own is indistinguishable from a quiet day.
  const unavailableReason =
    !team ? null
      : !team.slug ? "This team isn't set up for appointments yet, so it cannot take bookings."
        : eventTypes?.length === 0 ? "This team has no event type, so it publishes no slots."
          : null

  return {
    teams,
    team,
    columns,
    schedule,
    bookings,
    slotMinutes,
    bookingCount: bookings.filter(booking =>
      isLiveBooking(booking) && columns.some(c => booking.hosts.some(h => h.id === c.calUserId))).length,
    outsideWindowCount: outsideWindow.length,
    unhostedCount: unhosted.length,
    columnUnavailability,
    availabilityUnavailable: !!availabilityQuery.error,
    status: loadStatus,
    slotsLoading,
    error: failed ? "This team's appointments could not be loaded." : null,
    throttledError: firstCalThrottled(
      teamsQuery.error, membersQuery.error, usersQuery.error, eventTypesQuery.error, bookingsQuery.error,
      availabilityQuery.error),
    unavailableReason,
    refresh,
    isFetching: teamsQuery.isFetching || membersQuery.isFetching || usersQuery.isFetching
      || eventTypesQuery.isFetching || bookingsQuery.isFetching || availabilityQuery.isFetching,
  }
}
